// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faCheckCircle,
  faCancel,
  faPlus,
  faTrash,
  faSave,
  faClose,
  faArrowLeft,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";
import TopActionMenus from "./TopActionMenus";
import { useNavigationEvent } from "navigation-react";

const ls = new SecureLS({ encodingType: "aes" });

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Searchable Dropdown Component with fixed z-index
const SearchableProductDropdown = ({
  value,
  onChange,
  products,
  isSearching,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const dropdownRef = React.useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products || [];

    const term = searchTerm.toLowerCase();
    return (products || []).filter((product) => {
      const title = product.attributes.title?.toLowerCase() || "";
      const sku = product.attributes.field_sku?.toLowerCase() || "";
      return title.includes(term) || sku.includes(term);
    });
  }, [searchTerm, products]);

  // Find selected product when value changes
  useEffect(() => {
    if (value && products) {
      const found = products.find((p) => p.id === value);
      if (found) {
        setSelectedProduct(found);
        setSearchTerm(found.attributes.title);
      }
    } else {
      setSelectedProduct(null);
      setSearchTerm("");
    }
  }, [value, products]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleSelect = (product) => {
    onChange({ target: { name: "product_id", value: product.id } });
    setSelectedProduct(product);
    setSearchTerm(product.attributes.title);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange({ target: { name: "product_id", value: "" } });
    setSelectedProduct(null);
    setSearchTerm("");
    setIsOpen(false);
  };

  return (
    <div style={styles.searchableDropdown} ref={dropdownRef}>
      <div style={styles.searchContainer}>
        <div style={styles.searchIcon}>
          <FontAwesomeIcon icon={faSearch} />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search products by name or SKU..."
          style={styles.searchInput}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            style={styles.clearButton}
            title="Clear selection"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        )}
      </div>

      {isOpen && (
        <div style={styles.dropdownList}>
          {isSearching ? (
            <div style={styles.dropdownItem}>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading products...
            </div>
          ) : filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => handleSelect(product)}
                style={{
                  ...styles.dropdownItem,
                  ...(selectedProduct?.id === product.id
                    ? styles.selectedItem
                    : {}),
                }}
                title={`SKU: ${product.attributes.field_sku || "N/A"} | Stock: ${parseInt(product.attributes.field_stock_quantity) || 0}`}
              >
                <div style={styles.productInfo}>
                  <strong>{product.attributes.title}</strong>
                  <div style={styles.productDetails}>
                    <span style={styles.sku}>
                      SKU: {product.attributes.field_sku || "N/A"}
                    </span>
                    <span style={styles.stock}>
                      Stock:{" "}
                      {parseInt(product.attributes.field_stock_quantity) || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.noResults}>
              {searchTerm
                ? `No products found matching "${searchTerm}"`
                : "No products available"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ReceiveStock() {
  const { stateNavigator } = useNavigationEvent();

  const [loading, setLoading] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Main receipt data
  const [receiptData, setReceiptData] = useState({
    invoice_number: "",
    supplier_id: "",
    date_received: new Date().toISOString().split("T")[0],
    notes: "",
  });

  // Array for multiple items
  const [items, setItems] = useState([
    {
      product_id: "",
      quantity: 1,
      cost_price: 0,
      markup: 0, // Default 0%
    },
  ]);

  // Fetch products for dropdown
  const {
    data: productList,
    mutate: mutateProducts,
    isValidating: isLoadingProducts,
  } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_sku,field_stock_quantity&page[limit]=1000`,
    fetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  // Fetch suppliers for dropdown
  const { data: supplierData } = useSWR(
    `${process.env.REACT_APP_SUPPLIER_API}`,
    fetcher,
  );

  // Handle receipt data changes
  const handleReceiptChange = (e) => {
    const { name, value } = e.target;
    setReceiptData({
      ...receiptData,
      [name]: value,
    });
  };

  // Handle item changes
  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [name]: value,
    };
    setItems(newItems);
  };

  // Add new item row
  const addNewItem = () => {
    setItems([
      ...items,
      {
        product_id: "",
        quantity: 1,
        cost_price: 0,
        markup: 0,
      },
    ]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalQuantity = 0;
    let totalCost = 0;
    let totalSelling = 0;

    items.forEach((item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.cost_price) || 0;
      const markup = parseFloat(item.markup) || 0;
      const sellingPrice = cost * (1 + markup / 100);

      totalQuantity += quantity;
      totalCost += quantity * cost;
      totalSelling += quantity * sellingPrice;
    });

    return {
      totalQuantity,
      totalCost: totalCost.toFixed(2),
      totalSelling: totalSelling.toFixed(2),
      profit: (totalSelling - totalCost).toFixed(2),
      profitMarkup:
        totalCost > 0
          ? ((totalSelling / totalCost - 1) * 100).toFixed(1)
          : "0.0",
    };
  };

  const { totalQuantity, totalCost } = calculateTotals();

  // Handle receive stock submission
  const handleReceiveStock = async (e) => {
    e.preventDefault();

    // Validation
    if (!receiptData.invoice_number) {
      setErrorMessage("Invoice number is required");
      setModalError(true);
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.product_id) {
        setErrorMessage(`Please select product for item ${i + 1}`);
        setModalError(true);
        return;
      }
      if (item.quantity <= 0) {
        setErrorMessage(`Quantity must be greater than 0 for item ${i + 1}`);
        setModalError(true);
        return;
      }
      if (item.cost_price <= 0) {
        setErrorMessage(`Cost price must be greater than 0 for item ${i + 1}`);
        setModalError(true);
        return;
      }
      if (item.markup < 0) {
        setErrorMessage(`Markup cannot be negative for item ${i + 1}`);
        setModalError(true);
        return;
      }
    }

    setLoading(true);

    const csrfToken = await getCsrfToken();
    if (!csrfToken) {
      setErrorMessage("Authentication failed");
      setModalError(true);
      setLoading(false);
      return;
    }

    const debugInfo = {
      invoice: receiptData.invoice_number,
      items_count: items.length,
      first_item: items[0]
        ? {
            product_id: items[0].product_id,
            quantity: items[0].quantity,
            cost: items[0].cost_price,
            markup: items[0].markup,
          }
        : null,
    };

    try {
      // Format date for API
      const formatDateForAPI = (dateString) => {
        return `${dateString}T00:00:00+00:00`;
      };

      // Debug logging
      console.log("📤 Sending to Drupal:", debugInfo);

      // Create all stock movements
      const promises = items.map((item) => {
        const payload = {
          data: {
            type: "node--stock_movement",
            attributes: {
              title: `Stock Receipt - ${receiptData.invoice_number}`,
              field_quantity: parseInt(item.quantity),
              field_stock_movement_cost_price: parseFloat(item.cost_price),
              field_markup: parseFloat(item.markup),
              field_invoice_number: receiptData.invoice_number,
              field_date_received: formatDateForAPI(receiptData.date_received),
              field_movement_type: "received",
              field_notes: receiptData.notes || "",
            },
            relationships: {
              field_product: {
                data: {
                  type: "node--product",
                  id: item.product_id,
                },
              },
            },
          },
        };

        // Add supplier if selected
        if (receiptData.supplier_id) {
          payload.data.relationships.field_product_supplier = {
            data: {
              type: "node--supplier",
              id: receiptData.supplier_id,
            },
          };
        }

        // Log payload for debugging
        console.log("📦 Payload for item:", JSON.stringify(payload, null, 2));

        return fetch(`${process.env.REACT_APP_STOCK_MOVEMENT_API}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }).then(async (response) => {
          if (!response.ok) {
            // Get detailed error
            const errorText = await response.text();
            console.error("❌ API Error:", errorText);
            throw new Error(
              `Failed to create stock movement: ${response.status}`,
            );
          }
          const result = await response.json();
          console.log("✅ Success response:", result);
          return result;
        });
      });

      // Wait for all movements to be created
      await Promise.all(promises);

      // Refresh data
      mutateProducts();

      // Reset form
      setReceiptData({
        invoice_number: "",
        supplier_id: "",
        date_received: new Date().toISOString().split("T")[0],
        notes: "",
      });
      setItems([
        {
          product_id: "",
          quantity: 1,
          cost_price: 0,
          markup: 0,
        },
      ]);

      setSuccessMessage(
        `Stock received successfully! ${items.length} items added.`,
      );
      setModalSuccessMsg(true);
    } catch (err) {
      console.error("Error receiving stock:", err);
      setErrorMessage(err.message || "An error occurred while receiving stock");
      setModalError(true);
    }
    setLoading(false);
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="receive-stock-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Receive Stock"
            buttonLabel="Inventory"
            icon={faArrowLeft}
            onClick={() => stateNavigator.navigate("stockinventory")}
          />

          {/* RECEIPT FORM */}
          <div style={styles.card}>
            <form onSubmit={handleReceiveStock}>
              {/* Receipt Details */}
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Invoice Number *</label>
                  <input
                    type="text"
                    name="invoice_number"
                    value={receiptData.invoice_number}
                    onChange={handleReceiptChange}
                    style={styles.input}
                    placeholder="Enter invoice number"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Supplier</label>
                  <select
                    name="supplier_id"
                    value={receiptData.supplier_id}
                    onChange={handleReceiptChange}
                    style={styles.input}
                  >
                    <option value="">Select Supplier</option>
                    {supplierData?.data?.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.attributes.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Date Received</label>
                  <input
                    type="date"
                    name="date_received"
                    value={receiptData.date_received}
                    onChange={handleReceiptChange}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Items Table */}
              <div style={styles.itemsSection}>
                <div style={styles.itemsHeader}>
                  <button
                    type="button"
                    onClick={addNewItem}
                    style={styles.addButton}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Item
                  </button>
                </div>

                <div style={styles.tableContainer}>
                  <table style={styles.itemsTable}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Cost Price (GHC)</th>
                        <th>Markup %</th>
                        <th>Selling Price</th>
                        <th>Total Cost</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const cost = parseFloat(item.cost_price);
                        const markup = parseFloat(item.markup);
                        const quantity = parseFloat(item.quantity) || 0;
                        const sellingPrice = cost * (1 + markup / 100);
                        const totalCost = quantity * cost;

                        return (
                          <tr key={index}>
                            <td style={styles.td}>
                              <div style={{ position: "relative" }}>
                                <SearchableProductDropdown
                                  value={item.product_id}
                                  onChange={(e) => handleItemChange(index, e)}
                                  products={productList?.data}
                                  isSearching={isLoadingProducts}
                                />
                              </div>
                            </td>
                            <td style={styles.td}>
                              <input
                                type="number"
                                name="quantity"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, e)}
                                style={styles.input}
                                min="1"
                                required
                              />
                            </td>
                            <td style={styles.td}>
                              <input
                                type="number"
                                name="cost_price"
                                value={item.cost_price}
                                onChange={(e) => handleItemChange(index, e)}
                                style={styles.input}
                                step="0.01"
                                // min="0"
                                placeholder="0"
                                required
                              />
                            </td>
                            <td style={styles.td}>
                              <input
                                type="number"
                                name="markup"
                                value={item.markup}
                                onChange={(e) => handleItemChange(index, e)}
                                style={{ ...styles.input, width: "90px" }}
                                step="0.5"
                                // min="0"
                                placeholder="0"
                                max="1000"
                                required
                              />
                            </td>
                            <td style={styles.td}>
                              <strong style={{ color: "#28a745" }}>
                                GH₵{sellingPrice.toFixed(2)}
                              </strong>
                            </td>
                            <td style={styles.td}>
                              <strong>GH₵{totalCost.toFixed(2)}</strong>
                            </td>
                            <td style={styles.td}>
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  style={styles.removeButton}
                                  title="Remove item"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  name="notes"
                  value={receiptData.notes}
                  onChange={handleReceiptChange}
                  style={styles.textarea}
                  placeholder="Additional notes about this receipt"
                  rows="3"
                />
              </div>

              {/* Summary */}
              <div style={styles.summaryCard}>
                <h3 style={styles.summaryTitle}>Receipt Summary</h3>
                <div style={styles.summaryRow}>
                  <span>Number of Items:</span>
                  <strong>{items.length}</strong>
                </div>
                <div style={styles.summaryRow}>
                  <span>Total Quantity:</span>
                  <strong>{totalQuantity}</strong>
                </div>
                <div style={styles.summaryRow}>
                  <span>Total Cost:</span>
                  <strong>GH₵{totalCost}</strong>
                </div>
              </div>

              {/* Submit Button */}
              <div style={styles.submitSection}>
                <button
                  type="submit"
                  disabled={loading}
                  style={styles.submitButton}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} /> Receive Stock
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {/* Message Modals */}
      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    color: "#333",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  subtitle: {
    color: "#666",
    fontSize: "16px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    marginBottom: "20px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    marginBottom: "30px",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "8px",
    color: "rgb(51, 51, 51)",
  },
  input: {
    width: "100%",
    padding: "16px 16px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    boxSizing: "border-box",
    fontFamily: "Helvetica, Arial, sans-serif",
  },
  textarea: {
    width: "100%",
    padding: "16px 16px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    boxSizing: "border-box",
    resize: "vertical",
  },
  itemsSection: {
    marginBottom: "30px",
  },
  itemsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  },
  addButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  tableContainer: {
    overflowX: "auto",
  },
  itemsTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
    borderRadius: "8px",
  },
  th: {
    background: "#f8f9fa",
    padding: "12px",
    textAlign: "left",
    borderBottom: "2px solid #dee2e6",
    color: "#333",
    fontWeight: "600",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top",
    position: "relative", // Add this
    zIndex: "auto", // Add this
  },
  stockInfo: {
    display: "block",
    marginTop: "5px",
    color: "#666",
    fontSize: "12px",
  },
  removeButton: {
    background: "#dc3545",
    color: "white",
    border: "none",
    padding: "6px 12px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  summaryCard: {
    background: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    margin: "20px 0",
    border: "1px solid #dee2e6",
  },
  summaryTitle: {
    margin: "0 0 15px 0",
    color: "#333",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
    fontSize: "14px",
  },
  totalValue: {
    color: "#28a745",
    fontSize: "16px",
  },
  submitSection: {
    textAlign: "right",
    marginTop: "20px",
  },
  submitButton: {
    background: "#3D3D3D",
    color: "white",
    padding: "12px 30px",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    "&:disabled": {
      background: "#6c757d",
      cursor: "not-allowed",
    },
  },
  // Searchable dropdown styles
  searchableDropdown: {
    position: "relative",
    width: "100%",
    zIndex: 1000, // Add this to ensure proper stacking
  },
  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    color: "#666",
    zIndex: 2,
  },
  searchInput: {
    width: "100%",
    padding: "12px 12px 12px 40px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    boxSizing: "border-box",
    fontFamily: "Helvetica, Arial, sans-serif",
    backgroundColor: "white",
    zIndex: 1,
  },
  clearButton: {
    position: "absolute",
    right: "10px",
    background: "transparent",
    border: "none",
    color: "#666",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  dropdownList: {
    position: "fixed", // 🔥 instead of absolute
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "6px",
    maxHeight: "300px",
    overflowY: "auto",
    zIndex: 99999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  dropdownItem: {
    padding: "12px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
    fontSize: "14px",
    backgroundColor: "white", // Ensure background is set
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  selectedItem: {
    backgroundColor: "#e7f3ff !important",
    borderLeft: "3px solid #007bff",
  },
  productInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  productDetails: {
    display: "flex",
    gap: "12px",
    fontSize: "12px",
    color: "#666",
  },
  sku: {
    fontFamily: "monospace",
    backgroundColor: "#f8f9fa",
    padding: "2px 6px",
    borderRadius: "3px",
  },
  stock: {
    color: "#666",
  },
  noResults: {
    padding: "12px 16px",
    color: "#666",
    textAlign: "center",
    fontSize: "14px",
    backgroundColor: "white",
  },
};
